
import CheckCardGroup from '../parts/CheckCardGroup.vue'
import Callout from '../parts/Callout.vue'
import { fmtPhone } from '../parts/ConfirmRow.vue'
import AddressSearch from '~/components/AddressSearch.vue'
import TypedDateInput from '~/components/TypedDateInput.vue'

const COVERAGE_KEYS = ['va', 'tricare', 'champva', 'medicaid', 'fep_blue']

// Copy verbatim from DEMO's COVERAGE list (Survey.Redesign.Demo_7_31_6.html L1847-1854),
// mapped onto the real User boolean fields (flow/accounts/models.py).
const COVERAGE_OPTIONS = [
    { value: 'va', label: 'VA benefits', description: 'Veterans Affairs health care' },
    { value: 'tricare', label: 'TRICARE / TRICARE For Life', description: 'Military health benefits' },
    { value: 'champva', label: 'CHAMPVA', description: 'For families of veterans' },
    { value: 'medicaid', label: 'Medicaid', description: 'State coverage for limited income' },
    { value: 'fep_blue', label: 'FEP Blue / federal employee plan', description: 'Federal employee health benefits' },
    { value: 'none', label: 'None of these', none: true },
]

export default {
    components: { CheckCardGroup, Callout, AddressSearch, TypedDateInput },
    props: { target: { type: Object, required: true } },
    data () {
        return {
            // The "None of these" row has no backing target field of its own
            // — coverageValue/conditionsValue are derived from the User
            // boolean fields, which have no "none" flag among them, so
            // there's no way to reconstruct "none was explicitly chosen"
            // from target alone. These two local flags are the source of
            // truth for whether the none row should render checked.
            coverageNone: false,
            conditionsNone: false,
            // Which Medicare dates are half-typed, keyed by their label.
            pendingDates: { 'Part A': false, 'Part B': false },
        }
    },
    computed: {
        // usStates is the store getter (loaded once via loadUsStates on client
        // init, see store/index.js:3498) — same getter pages/survey/_uid/demographics.vue
        // uses for its state selects; mirrors its exact source.
        states () { return this.$store.getters.usStates || [] },
        conditions () { return this.$store.getters.possibleCsnpConditions || [] },
        coverageOptions () { return COVERAGE_OPTIONS },
        conditionOptions () {
            return [
                ...this.conditions.map(c => ({ value: c.slug, label: c.name })),
                { value: 'none', label: 'None of these', none: true },
            ]
        },
        coverageValue () {
            const values = COVERAGE_KEYS.filter(k => this.target[k])
            return this.coverageNone ? [...values, 'none'] : values
        },
        conditionsValue () {
            const values = this.conditions.map(c => c.slug).filter(slug => this.target[slug])
            return this.conditionsNone ? [...values, 'none'] : values
        },
        noMedicareYet () { return this.target.medicare_parts === 'NONE' || !this.target.medicare_parts },
    },
    methods: {
        fmtPhone,
        // #689: all five permanent fields from one pick. The state <select>
        // holds null when empty, the text inputs hold ''.
        onAddressPick (a) {
            this.$set(this.target, 'street_1', a.street_1 || '')
            this.$set(this.target, 'street_2', a.street_2 || '')
            this.$set(this.target, 'city', a.city || '')
            this.$set(this.target, 'state', a.state || null)
            this.$set(this.target, 'zip_code', a.zip_code || '')
        },
        onMailingPick (a) {
            this.$set(this.target, 'mailing_street_1', a.street_1 || '')
            this.$set(this.target, 'mailing_street_2', a.street_2 || '')
            this.$set(this.target, 'mailing_city', a.city || '')
            this.$set(this.target, 'mailing_state', a.state || null)
            this.$set(this.target, 'mailing_zip_code', a.zip_code || '')
        },
        // '' (a cleared date input) is not a date -- store null so the field can
        // actually be emptied. These are optional dates; blank is a legitimate
        // answer for a client who hasn't enrolled yet.
        setDate (key, value) {
            this.$set(this.target, key, value || null)
        },
        // Tells the page which date (if any) is half-typed, so it can say so.
        onDateIncomplete (label, incomplete) {
            this.pendingDates[label] = incomplete
            const first = Object.keys(this.pendingDates).find(k => this.pendingDates[k])
            this.$emit('date-incomplete', first || null)
        },
        onCoverageInput (values) {
            const hasReal = values.some(v => v !== 'none')
            this.coverageNone = !hasReal && values.includes('none')
            COVERAGE_KEYS.forEach(k => this.$set(this.target, k, !this.coverageNone && values.includes(k)))
        },
        onConditionsInput (values) {
            const hasReal = values.some(v => v !== 'none')
            this.conditionsNone = !hasReal && values.includes('none')
            this.conditions.forEach(c => this.$set(this.target, c.slug, !this.conditionsNone && values.includes(c.slug)))
        },
    },
}
